import React from 'react';
import { Box, Card, Grid, Chip, Divider, Container, IconButton, Paper, Button, Typography, Skeleton, Popper, LinearProgress, Tooltip as MuiTooltip } from '@mui/material';
import MainContainer from 'theme/MainConatiner';
import CustomFilterButton from 'components/Fileds/CustomFilterButton';
import CategoryCard from 'components/CategoryCard/CategoryCard';
import CategoryCardSkeleton from 'components/CategoryCard/CategoryCardSkeleton';
import CommonDataGrid from 'components/TableListing/CommonTable';
import NoItemsCreated from 'components/NoItemsCreated/NoItemsCreated';
import NoSearchFound from 'components/NoSearchFound/NoSearchFound';
import IOSSwitch from 'components/Fileds/IOSSwitch';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import InventoryIcon from '@mui/icons-material/Inventory';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CustomTypography from 'components/Fileds/CustomTypography';
import CustomSearch from 'components/Fileds/CustomSearch';
import CustomSelect from 'components/Fileds/CustomSelect';
import CustomButton from 'components/Fileds/CustomButton';

// Additional imports for Inventory Dashboard
import { useSelector } from 'react-redux';
import { formatCurrency } from 'utils/function';
import { startOfWeek, endOfWeek, subWeeks, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, format } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import TuneIcon from '@mui/icons-material/Tune';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import toast from 'react-hot-toast';
import { ComposedChart, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend, AreaChart, Area } from 'recharts';
import { TableSkeleton } from 'utils/constant';

const InventoryContainer = ({
  selectedTab,
  setSelectedTab,
  searchQuery,
  setSearchQuery,
  debouncedSearchQuery,
  // Categories
  categories = [],
  categoriesLoading,
  categoryTogglingId,
  // Items
  items = [],
  itemsLoading,
  itemsCount = 0,
  itemTogglingId,
  // Recipes
  recipes = [],
  recipesLoading,
  recipesCount = 0,
  // Wastage
  wastage = [],
  wastagesLoading,
  wastageCount = 0,
  // Stock Adjustments
  adjustments = [],
  adjustmentsLoading,
  adjustmentsCount = 0,
  // Actions
  handleAddBtn,
  handleEdit,
  handleDelete,
  handleToggleStatus,
  handleViewIngredients,
  handleQuickAdjustment,
  // Category Filtering for Items
  selectedCategoryFilter,
  setSelectedCategoryFilter,
  // Status Filtering for Items
  selectedStatusFilter,
  setSelectedStatusFilter,
  // Pagination
  paginationModel,
  setPaginationModel,
  // Dashboard Props
  dashboardDateRange,
  setDashboardDateRange,
  dashboardData = {},
  dashboardLoading,
}) => {
  const isSearching = searchQuery.trim().length > 0;

  // Filter categories based on frontend search if loading is complete
  const filteredCategories = categories.filter(cat =>
    cat.name?.toLowerCase().includes((debouncedSearchQuery || searchQuery).toLowerCase())
  );

  const currency = useSelector((state) => state?.posData?.vendor?.currency);

  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const presets = React.useMemo(() => [
    {
      label: 'Current Week',
      range: {
        startDate: startOfWeek(new Date(), { weekStartsOn: 1 }),
        endDate: endOfWeek(new Date(), { weekStartsOn: 1 }),
      }
    },
    {
      label: 'Previous Week',
      range: {
        startDate: startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
        endDate: endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
      }
    },
    {
      label: 'Current Month',
      range: {
        startDate: startOfMonth(new Date()),
        endDate: endOfMonth(new Date())
      }
    },
    {
      label: 'Previous Month',
      range: {
        startDate: startOfMonth(subMonths(new Date(), 1)),
        endDate: endOfMonth(subMonths(new Date(), 1))
      }
    },
    {
      label: 'Current Year',
      range: {
        startDate: startOfYear(new Date()),
        endDate: endOfYear(new Date())
      }
    }
  ], []);

  const handleButtonClick = (e) => {
    setAnchorEl(e.currentTarget);
    setPickerOpen((open) => !open);
  };

  const handleSelect = (ranges) => {
    setDashboardDateRange({ ...ranges.selection, color: '#000' });
    setPickerOpen(false);
  };

  const monthStaticRanges = React.useMemo(() => presets.map(preset => ({
    label: preset.label,
    range: () => ({
      startDate: preset.range.startDate,
      endDate: preset.range.endDate,
    }),
    isSelected: range =>
      range.startDate?.getTime() === preset.range.startDate.getTime() &&
      range.endDate?.getTime() === preset.range.endDate.getTime(),
    color: '#3d91ff'
  })), [presets]);

  // Setup tab buttons
  const tabs = [
    { value: 'dashboard', label: 'Dashboard' },
    { value: 'categories', label: 'Categories' },
    { value: 'items', label: 'Items' },
    { value: 'recipes', label: 'Recipes' },
    { value: 'wastage', label: 'Wastage' },
    { value: 'adjustments', label: 'Stock Adjustment' },
  ];

  // Helper to check if any content exists
  const showCategoriesEmpty = !categoriesLoading && filteredCategories.length === 0;
  const showItemsEmpty = !itemsLoading && items.length === 0;
  const showAdjustmentsEmpty = !adjustmentsLoading && adjustments.length === 0;
  const showRecipesEmpty = !recipesLoading && recipes.length === 0;
  const showWastageEmpty = !wastagesLoading && wastage.length === 0;

  // Setup columns for Items DataGrid
  const itemColumns = [
    {
      headerName: 'SKU Number',
      field: 'sku_number',
      flex: 1,
      minWidth: 150,
      renderCell: ({ row }) => (
        <Box sx={{ paddingLeft: "10px", fontWeight: 600 }}>
          {row.sku_number || 'N/A'}
        </Box>
      )
    },
    {
      headerName: 'Item Name',
      field: 'name',
      flex: 1.5,
      minWidth: 200,
      renderCell: ({ row }) => (
        <Box sx={{ paddingLeft: "10px" }}>
          {row.name || 'N/A'}
        </Box>
      )
    },
    {
      headerName: 'Category',
      field: 'category',
      flex: 1.2,
      minWidth: 150,
      renderCell: ({ row }) => (
        <Box sx={{ paddingLeft: "10px" }}>
          {row.category?.name || 'N/A'}
        </Box>
      )
    },
    {
      headerName: 'Unit Type',
      field: 'unit_type',
      flex: 1,
      minWidth: 120,
      renderCell: ({ row }) => (
        <Box sx={{ paddingLeft: "10px" }}>
          {row.unit_type || 'N/A'}
        </Box>
      )
    },
    {
      headerName: 'Stock',
      field: 'stock',
      flex: 1,
      minWidth: 120,
      renderCell: ({ row }) => {
        const stockVal = row.stock != null ? parseFloat(row.stock) : 0;
        const formattedStock = row.stock != null ? parseFloat(row.stock).toFixed(2) : '0.00';

        let statusText = '';
        let textColor = '';

        const lowStockVal = row.low_stock_val != null ? parseFloat(row.low_stock_val) : 10;

        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const isExpired = row.expiry_date && new Date(row.expiry_date) < todayStart;

        if (isExpired) {
          statusText = '(Expired)';
          textColor = '#dc2828'; // red
        } else if (stockVal <= 0) {
          statusText = '(Out of Stock)';
          textColor = '#EF4444'; // red
        } else if (stockVal < lowStockVal) {
          statusText = '(Low Stock)';
          textColor = '#F97316'; // dark yellow / amber
        }

        return (
          <Box sx={{
            paddingLeft: "10px",
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            position: 'relative',
            width: '100%'
          }}>
            <Box sx={{ marginBottom: '8px' }}>{formattedStock}</Box>
            {statusText && (
              <Box sx={{
                color: textColor,
                fontSize: '11px',
                fontWeight: 600,
                position: 'absolute',
                bottom: '8px',
                left: '10px',
                lineHeight: '1'
              }}>
                {statusText}
              </Box>
            )}
          </Box>
        );
      }
    },
    {
      headerName: 'Status',
      field: 'status',
      flex: 1,
      minWidth: 120,
      renderCell: ({ row }) => (
        <Box sx={{ paddingLeft: "10px" }}>
          <IOSSwitch
            checked={row.active_status}
            onChange={() => handleToggleStatus(row)}
            disabled={itemTogglingId === row.id}
          />
        </Box>
      )
    },
    {
      headerName: 'Actions',
      field: 'action',
      minWidth: 140,
      renderCell: ({ row }) => {
        const stockVal = row.stock != null ? parseFloat(row.stock) : 0;
        const lowStockVal = row.low_stock_val != null ? parseFloat(row.low_stock_val) : 10;

        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const isExpired = row.expiry_date && new Date(row.expiry_date) < todayStart;

        const shouldShowCart = stockVal <= 0 || stockVal < lowStockVal || isExpired;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: '100%' }}>
            {/* {shouldShowCart && ( */}
            <ShoppingCartIcon
              onClick={() => handleQuickAdjustment?.(row)}
              fontSize="small"
              sx={{ color: '#B78103', mr: 2, cursor: 'pointer', visibility: shouldShowCart ? "visible" : "hidden", }}
            />
            {/* )} */}
            <EditIcon onClick={() => handleEdit(row)} fontSize="small" sx={{ color: '#1297DE', mr: 2, cursor: 'pointer' }} />
            <DeleteIcon onClick={() => handleDelete(row)} fontSize="small" sx={{ color: '#FF0202', cursor: 'pointer' }} />
          </Box>
        );
      }
    }
  ];

  // Setup columns for Recipes DataGrid
  const recipeColumns = [
    {
      headerName: 'Food Item',
      field: 'name',
      flex: 1.2,
      minWidth: 200,
      renderCell: ({ row }) => (
        <Box sx={{ paddingLeft: "10px", fontWeight: 600 }}>
          {row.name || 'N/A'}
        </Box>
      )
    },
    {
      headerName: 'Ingredients List',
      field: 'recipeIngredients',
      flex: 3,
      minWidth: 400,
      renderCell: ({ row }) => {
        const ings = row.recipeIngredients || [];
        const mainIngs = ings.filter(ing => !ing.addon_id);
        const displayIngs = mainIngs.length > 0 ? mainIngs : ings;
        const count = displayIngs.length;
        const countText = `${count} ${count === 1 ? 'Ingredient' : 'Ingredients'}`;

        const names = displayIngs
          .map(ing => ing.inventoryItem?.name || ing.name)
          .filter(Boolean);

        const maxVisible = 5;
        let bracketText = '';
        if (names.length > 0) {
          if (names.length > maxVisible) {
            bracketText = ` (${names.slice(0, maxVisible).join(', ')}, ...)`;
          } else {
            bracketText = ` (${names.join(', ')})`;
          }
        }

        const fullTooltipText = names.length > 0
          ? `${countText} (${names.join(', ')})`
          : countText;

        return (
          // <MuiTooltip title={names.length > maxVisible ? fullTooltipText : ''} arrow placement="top">
            <Box
              sx={{
                paddingLeft: '10px',
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%'
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontWeight: 500,
                  fontSize: '14px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                <span>{countText}</span>
                {bracketText && (
                  <span style={{ color: '#000', marginLeft: '4px' }}>
                    {bracketText}
                  </span>
                )}
              </Typography>
            </Box>
          // </MuiTooltip>
        );
      }
    },
    {
      headerName: 'Actions',
      field: 'action',
      minWidth: 120,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: '100%' }}>
          <VisibilityIcon fontSize="small" sx={{ cursor: 'pointer', mr: 2 }}
            onClick={() => handleViewIngredients(row)}
          />
          <EditIcon onClick={() => handleEdit(row)} fontSize="small" sx={{ color: '#1297DE', mr: 2, cursor: 'pointer' }} />
          <DeleteIcon onClick={() => handleDelete(row)} fontSize="small" sx={{ color: '#FF0202', cursor: 'pointer' }} />
        </Box>
      )
    }
  ];

  // Setup real API-driven columns for Wastage reports
  const wastageColumns = [
    {
      headerName: 'Date',
      field: 'created_at',
      flex: 1,
      minWidth: 150,
      renderCell: ({ row }) => (
        <Box sx={{ paddingLeft: "10px" }}>
          {row.created_at
            ? new Date(row.created_at).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
            : "-"}
        </Box>
      )
    },
    {
      headerName: 'Item Name',
      field: 'inventoryItem',
      flex: 1.5,
      minWidth: 200,
      renderCell: ({ row }) => <Box sx={{ paddingLeft: "10px" }}>{row.inventoryItem?.name || '-'}</Box>
    },
    {
      headerName: 'Qty Wasted',
      field: 'qty',
      flex: 1,
      minWidth: 120,
      renderCell: ({ row }) => (
        <Box sx={{ paddingLeft: "10px" }}>
          {row.qty} {row.unit_type || row.inventoryItem?.unit_type || ''}
        </Box>
      )
    },
    {
      headerName: 'Reason',
      field: 'reason',
      flex: 1.5,
      minWidth: 150,
      renderCell: ({ row }) => {
        const val = row.reason || '-';
        if (val === '-') return <Box sx={{ paddingLeft: "10px" }}>-</Box>;
        return (
          <MuiTooltip title={val} arrow placement="top">
            <Box sx={{ paddingLeft: "10px", width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}>
              {val}
            </Box>
          </MuiTooltip>
        );
      }
    },
    {
      headerName: 'Notes',
      field: 'notes',
      flex: 2,
      minWidth: 220,
      renderCell: ({ row }) => {
        const val = row.notes || '-';
        if (val === '-') return <Box sx={{ paddingLeft: "10px" }}>-</Box>;
        return (
          <MuiTooltip title={val} arrow placement="top">
            <Box sx={{ paddingLeft: "10px", width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}>
              {val}
            </Box>
          </MuiTooltip>
        );
      }
    },
    {
      headerName: 'Actions',
      field: 'action',
      minWidth: 120,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: '100%' }}>
          <EditIcon onClick={() => handleEdit(row)} fontSize="small" sx={{ color: '#1297DE', mr: 2, cursor: 'pointer' }} />
          <DeleteIcon onClick={() => handleDelete(row)} fontSize="small" sx={{ color: '#FF0202', cursor: 'pointer' }} />
        </Box>
      )
    }
  ];

  const adjustmentColumns = [
    {
      headerName: 'Date',
      field: 'created_at',
      flex: 1,
      minWidth: 150,
      renderCell: ({ row }) => (
        <Box sx={{ paddingLeft: "10px" }}>
          {row.created_at
            ? new Date(row.created_at).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
            : "-"}
        </Box>
      )
    },
    {
      headerName: 'Item Name',
      field: 'inventoryItem',
      flex: 1.5,
      minWidth: 200,
      renderCell: ({ row }) => <Box sx={{ paddingLeft: "10px" }}>{row.inventoryItem?.name || '-'}</Box>
    },
    {
      headerName: 'Qty Adjusted',
      field: 'qty',
      flex: 1,
      minWidth: 150,
      renderCell: ({ row }) => {
        const sign = row.type === 'add' ? '+' : '-';
        const color = row.type === 'add' ? '#10B981' : '#EF4444'; // Emerald/Green vs Red
        return (
          <Box sx={{ paddingLeft: "10px", color, fontWeight: 600 }}>
            {sign}{row.qty} {row.unit_type || row.inventoryItem?.unit_type || ''}
          </Box>
        );
      }
    },
    {
      headerName: 'Reason',
      field: 'reason',
      flex: 2,
      minWidth: 250,
      renderCell: ({ row }) => {
        const val = row.reason || '-';
        if (val === '-') return <Box sx={{ paddingLeft: "10px" }}>-</Box>;
        return (
          <MuiTooltip title={val} arrow placement="top">
            <Box sx={{ paddingLeft: "10px", width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}>
              {val}
            </Box>
          </MuiTooltip>
        );
      }
    },
    {
      headerName: 'Actions',
      field: 'action',
      minWidth: 120,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: '100%' }}>
          <EditIcon onClick={() => handleEdit(row)} fontSize="small" sx={{ color: '#1297DE', mr: 2, cursor: 'pointer' }} />
          <DeleteIcon onClick={() => handleDelete(row)} fontSize="small" sx={{ color: '#FF0202', cursor: 'pointer' }} />
        </Box>
      )
    }
  ];

  return (
    <MainContainer padding="0px">
      {/* Sticky Tab Buttons & Search/Filters Header */}
      <Box
        sx={{
          // position: 'sticky', 
          // top: 65, 
          // zIndex: 1100, 
          bgcolor: '#fff',
          pt: 2,
          // pb: 1
        }}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            maxWidth: "1400px",
            mx: "auto",
            px: { xs: 1, sm: 5, md: 6 },
          }}
        >
          <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-start', paddingBottom: "5px" }}>
            {tabs.map((tab) => (
              <CustomFilterButton
                key={tab.value}
                label={tab.label}
                color="#000000"
                backgroundColor="#000000"
                selected={selectedTab === tab.value}
                onClick={() => setSelectedTab(tab.value)}
                height={"33px"}
                border={`1px solid`}
              />
            ))}
          </Box>
          {/* <Divider sx={{ my: 0.5, borderColor: '#E5E7EB' }} /> */}

          {selectedTab === 'dashboard' && (
            <Card
              sx={{
                p: 2,
                mt: 2,
                mb: 1.5,
                boxShadow: 'none',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                backgroundColor: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827' }}>Inventory Performance Dashboard</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>Track stock usage, wastage, and movement analytics.</Typography>
              </Box>

              {/* Date Range Picker Button */}
              <Box sx={{ position: 'relative' }}>
                <Button
                  variant="outlined"
                  startIcon={<CalendarMonthIcon />}
                  onClick={handleButtonClick}
                  sx={{
                    textTransform: 'none',
                    lineHeight: '1.5',
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                    color: 'text.primary',
                    minWidth: '220px',
                    justifyContent: 'flex-start',
                    height: '40px',
                    borderRadius: '10px'
                  }}
                >
                  {dashboardDateRange?.startDate && dashboardDateRange?.endDate
                    ? `${format(dashboardDateRange.startDate, 'MMM dd, yyyy')} - ${format(dashboardDateRange.endDate, 'MMM dd, yyyy')}`
                    : 'Select Date Range'
                  }
                </Button>

                <Popper
                  open={pickerOpen}
                  anchorEl={anchorEl}
                  placement="bottom-end"
                  onClose={() => setPickerOpen(false)}
                  modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
                  sx={{ zIndex: 1300 }}
                >
                  <Paper elevation={3} sx={{ p: 1, mt: 1 }}>
                    <DateRangePicker
                      locale={enUS}
                      onChange={handleSelect}
                      months={2}
                      direction="horizontal"
                      showDateDisplay={false}
                      showPreview={false}
                      staticRanges={monthStaticRanges}
                      inputRanges={[]}
                      initialFocusedRange={[0, 0]}
                      moveRangeOnFirstSelection={false}
                      ranges={[dashboardDateRange.startDate ? dashboardDateRange : {
                        startDate: new Date(),
                        endDate: new Date(),
                        key: 'selection',
                        color: '#000'
                      }]}
                    />
                  </Paper>
                </Popper>
              </Box>
            </Card>
          )}

          {/* 2) Search & Filters Card Container */}
          {['categories', 'items', 'recipes', 'wastage', 'adjustments'].includes(selectedTab) && (
            <Card
              sx={{
                py: 2,
                // mb: 1,
                boxShadow: 'none',
                // border: '1px solid #E5E7EB',
                borderRadius: '12px',
                backgroundColor: 'transparent',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                {/* Left Side: Search Bar (smaller width) */}
                <Box sx={{ flex: 1, width: { xs: '100%', sm: '380px' } }}>
                  <CustomSearch
                    onSearchChange={setSearchQuery}
                    searchQuery={searchQuery}
                    placeholder={
                      selectedTab === 'items'
                        ? "Search Inventory Items..."
                        : selectedTab === 'categories'
                          ? "Search Categories..."
                          : selectedTab === 'recipes'
                            ? "Search Recipes..."
                            : selectedTab === 'wastage'
                              ? "Search Wastage..."
                              : "Search Stock Adjustments..."
                    }
                    isLoading={false}
                  />
                </Box>

                {/* Right Side: Select Filter & Add Button */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
                  {selectedTab === 'items' && (
                    <>
                      <Box sx={{ minWidth: 160 }}>
                        <CustomSelect
                          name="status"
                          options={[
                            { value: 'all', label: 'All' },
                            { value: 'expired', label: 'Expired' },
                            { value: 'outofstock', label: 'Out of Stock' },
                            { value: 'lowstock', label: 'Low Stock' }
                          ]}
                          value={selectedStatusFilter ?? 'all'}
                          onChange={(e) => setSelectedStatusFilter?.(e.target.value)}
                          sx={{ borderRadius: '10px', backgroundColor: '#fff', fontSize: '14px', height: 40 }}
                        />
                      </Box>
                      {categories.length > 0 && (
                        <Box sx={{ minWidth: 160 }}>
                          <CustomSelect
                            name="category"
                            options={[
                              { value: '', label: 'All Categories' },
                              ...(categories ?? []).map((cat) => ({
                                value: cat.id,
                                label: cat.name,
                              }))
                            ]}
                            value={selectedCategoryFilter ?? ''}
                            onChange={(e) => setSelectedCategoryFilter?.(e.target.value)}
                            sx={{ borderRadius: '10px', backgroundColor: '#fff', fontSize: '14px', height: 40 }}
                          />
                        </Box>
                      )}
                    </>
                  )}

                  <CustomButton
                    sx={{
                      borderRadius: "10px",
                      height: 40,
                      px: 3,
                      backgroundColor: '#000000',
                      color: '#ffffff',
                      '&:hover': {
                        backgroundColor: '#222222',
                      }
                    }}
                    onClick={handleAddBtn}
                    startIcon={<AddIcon sx={{ color: '#ffffff' }} />}
                  >
                    {selectedTab === 'items'
                      ? "Add Item"
                      : selectedTab === 'categories'
                        ? "Add Category"
                        : selectedTab === 'recipes'
                          ? "Add Recipe"
                          : selectedTab === 'wastage'
                            ? "Record Wastage"
                            : "Add Adjustment"
                    }
                  </CustomButton>
                </Box>
              </Box>

              {/* <Divider sx={{ mt: 2 }} /> */}
            </Card>
          )}
        </Container>
      </Box >

      <Container
        maxWidth={false}
        disableGutters
        sx={{
          maxWidth: "1400px",
          mx: "auto",
          px: { xs: 1, sm: 5, md: 6 },
          pb: 1,
        }}
      >
        {/* 3) Dynamic Content Area */}
        {selectedTab === 'dashboard' && dashboardLoading && (
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {[1, 2, 3, 4].map((i) => (
                  <Grid item xs={12} sm={6} md={3} key={i}>
                    <Skeleton variant="rectangular" height={100} sx={{ borderRadius: '12px' }} />
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rectangular" height={350} sx={{ borderRadius: '12px' }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rectangular" height={350} sx={{ borderRadius: '12px' }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rectangular" height={350} sx={{ borderRadius: '12px' }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rectangular" height={350} sx={{ borderRadius: '12px' }} />
            </Grid>
          </Grid>
        )}

        {selectedTab === 'dashboard' && !dashboardLoading && (
          <Box sx={{ mt: 1 }}>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {/* Total Active Items */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none', backgroundColor: '#fff' }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: '#E8F0FE', color: '#1A73E8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <InventoryIcon sx={{ fontSize: 22 }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="caption" sx={{ color: '#1A73E8', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Items</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.25, color: '#111827', lineHeight: 1.2 }}>
                      {dashboardData.summaryCards?.totalItems || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.25, fontSize: '11px' }}>All Inventory Items</Typography>
                  </Box>
                </Card>
              </Grid>

              {/* Out of Stock */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none', backgroundColor: '#fff' }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: '#FCE8E6', color: '#C5221F', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <ErrorIcon sx={{ fontSize: 22 }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="caption" sx={{ color: '#C5221F', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Out of Stock</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.25, color: '#111827', lineHeight: 1.2 }}>
                      {dashboardData.summaryCards?.outOfStockItems || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.25, fontSize: '11px' }}>Out of Stock</Typography>
                  </Box>
                </Card>
              </Grid>

              {/* Low Stock */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none', backgroundColor: '#fff' }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: '#FEF3C7', color: '#D97706', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <WarningIcon sx={{ fontSize: 22 }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="caption" sx={{ color: '#F97316', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Low Stock</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.25, color: '#111827', lineHeight: 1.2 }}>
                      {dashboardData.stockStatus?.legend?.lowStock || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.25, fontSize: '11px' }}>Reorder Soon</Typography>
                  </Box>
                </Card>
              </Grid>

              {/* Expiring Soon */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none', backgroundColor: '#fff' }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: '#FFF7ED', color: '#EA580C', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <CalendarMonthIcon sx={{ fontSize: 22 }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="caption" sx={{ color: '#EA580C', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Expiring (7 Days)</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.25, color: '#111827', lineHeight: 1.2 }}>
                      {dashboardData.summaryCards?.expiringSoonItems || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.25, fontSize: '11px' }}>Within 7 Days</Typography>
                  </Box>
                </Card>
              </Grid>
            </Grid>

            {/* Dashboard Charts / Tables Grid */}
            <Grid container spacing={3}>
              {/* Pie Chart: Stock Status Breakdown */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none', backgroundColor: '#fff', height: 420, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Stock Status Breakdown</Typography>
                  {(() => {
                    const totalPieItems = (dashboardData.stockStatus?.legend?.inStock || 0) +
                      (dashboardData.stockStatus?.legend?.lowStock || 0) +
                      (dashboardData.stockStatus?.legend?.outOfStock || 0) +
                      (dashboardData.stockStatus?.legend?.expiringSoon || 0) +
                      (dashboardData.stockStatus?.legend?.expired || 0);

                    const hasPieData = totalPieItems > 0;

                    const pieDataList = [
                      { name: 'In Stock', value: dashboardData.stockStatus?.pieChart?.inStock || 0, color: '#10B981', count: dashboardData.stockStatus?.legend?.inStock || 0 },
                      { name: 'Low Stock', value: dashboardData.stockStatus?.pieChart?.lowStock || 0, color: '#F59E0B', count: dashboardData.stockStatus?.legend?.lowStock || 0 },
                      { name: 'Out of Stock', value: dashboardData.stockStatus?.pieChart?.outOfStock || 0, color: '#EF4444', count: dashboardData.stockStatus?.legend?.outOfStock || 0 },
                      { name: 'Expiring Soon', value: dashboardData.stockStatus?.pieChart?.expiringSoon || 0, color: '#F97316', count: dashboardData.stockStatus?.legend?.expiringSoon || 0 },
                      { name: 'Expired', value: dashboardData.stockStatus?.pieChart?.expired || 0, color: '#991B1B', count: dashboardData.stockStatus?.legend?.expired || 0 }
                    ];

                    const activePieData = pieDataList.filter(item => item.value > 0);

                    if (!hasPieData) {
                      return (
                        <Box sx={{ display: 'flex', flexGrow: 1, alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                          No stock status data available.
                        </Box>
                      );
                    }

                    return (
                      <Grid container spacing={2} sx={{ flexGrow: 1, alignItems: 'center' }}>
                        <Grid item xs={12} sm={6} sx={{ height: 260, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={activePieData}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={85}
                                paddingAngle={3}
                                dataKey="value"
                              >
                                {activePieData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                            </PieChart>
                          </ResponsiveContainer>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {pieDataList.map((item, index) => {
                              const percentage = totalPieItems > 0 ? ((item.count / totalPieItems) * 100).toFixed(1) : '0.0';
                              return (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: item.color }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>{item.name}</Typography>
                                  </Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#6B7280' }}>
                                    {item.count}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        </Grid>
                      </Grid>
                    );
                  })()}
                </Card>
              </Grid>

              {/* Area Chart: Stock Movement */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none', backgroundColor: '#fff', height: 420, display: 'flex', flexDirection: 'column' }}>
                  {(() => {
                    const isToday = dashboardDateRange?.startDate && dashboardDateRange?.endDate &&
                      format(new Date(dashboardDateRange.startDate), 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd') &&
                      format(new Date(dashboardDateRange.endDate), 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

                    const movementChartData = dashboardData.stockMovement?.chart || [];
                    const hasMovementData = movementChartData.length > 0;

                    return (
                      <>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                          Stock Movement ({isToday ? 'Today' : 'Selected Period'})
                        </Typography>

                        {/* Metrics Summary Row */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, pb: 1, borderBottom: '1px solid #F3F4F6', flexWrap: 'nowrap', overflowX: 'auto', gap: 1 }}>
                          {/* Stock In */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: '60px' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, fontSize: '11px', whiteSpace: 'nowrap' }}>Stock In</Typography>
                            <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#E6F4EA', color: '#137333', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                              <ArrowDownwardIcon sx={{ fontSize: 18 }} />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#111827' }}>
                              {dashboardData.stockMovement?.summary?.stockIn?.toFixed(2) || '0.00'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px' }}>Qty</Typography>
                          </Box>

                          <Divider orientation="vertical" flexItem sx={{ borderColor: '#E5E7EB', height: 40, my: 'auto' }} />

                          {/* Stock Out */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: '60px' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, fontSize: '11px', whiteSpace: 'nowrap' }}>Stock Out</Typography>
                            <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#FCE8E6', color: '#C5221F', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                              <ArrowUpwardIcon sx={{ fontSize: 18 }} />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#111827' }}>
                              {dashboardData.stockMovement?.summary?.stockOut?.toFixed(2) || '0.00'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px' }}>Qty</Typography>
                          </Box>

                          <Divider orientation="vertical" flexItem sx={{ borderColor: '#E5E7EB', height: 40, my: 'auto' }} />

                          {/* Adjustments */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: '60px' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, fontSize: '11px', whiteSpace: 'nowrap' }}>Adjustments</Typography>
                            <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#FEF3C7', color: '#D97706', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                              <TuneIcon sx={{ fontSize: 18 }} />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#111827' }}>
                              {dashboardData.stockMovement?.summary?.adjustments?.toFixed(2) || '0.00'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px' }}>Qty</Typography>
                          </Box>

                          <Divider orientation="vertical" flexItem sx={{ borderColor: '#E5E7EB', height: 40, my: 'auto' }} />

                          {/* Wastage */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: '60px' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, fontSize: '11px', whiteSpace: 'nowrap' }}>Wastage</Typography>
                            <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#F3E8FF', color: '#7C3AED', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#111827' }}>
                              {dashboardData.stockMovement?.summary?.wastage?.toFixed(2) || '0.00'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px' }}>Qty</Typography>
                          </Box>
                        </Box>

                        {/* Chart Area */}
                        {!hasMovementData ? (
                          <Box sx={{ display: 'flex', flexGrow: 1, alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                            No stock movement data registered in this period.
                          </Box>
                        ) : (
                          <Box sx={{ flexGrow: 1, width: '100%', minHeight: 0 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={movementChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <defs>
                                  <linearGradient id="colorNetMovement" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.01} />
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#6B7280' }} tickLine={false} axisLine={false} />
                                <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} tickLine={false} axisLine={false} />
                                <Tooltip formatter={(value) => [`${parseFloat(value).toFixed(2)} Qty`, 'Net Movement']} />
                                <Area type="monotone" dataKey="netMovement" stroke="#8B5CF6" strokeWidth={2} fillOpacity={1} fill="url(#colorNetMovement)" dot={{ r: 3, fill: '#8B5CF6', strokeWidth: 1 }} activeDot={{ r: 5 }} />
                              </AreaChart>
                            </ResponsiveContainer>
                          </Box>
                        )}
                      </>
                    );
                  })()}
                </Card>
              </Grid>

              {/* Top Consumed Ingredients */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none', backgroundColor: '#fff', height: 420, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Top Consumed Ingredients</Typography>
                    <Chip label="Last 7 Days Trend" size="small" sx={{ fontSize: '11px', fontWeight: 600, bgcolor: '#F3F4F6', color: '#4B5563' }} />
                  </Box>
                  {(() => {
                    const topIngredients = dashboardData.topConsumedIngredients || [];
                    if (topIngredients.length === 0) {
                      return (
                        <Box sx={{ display: 'flex', flexGrow: 1, alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                          No ingredient consumption recorded.
                        </Box>
                      );
                    }

                    return (
                      <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                              <th style={{ padding: '10px 12px', fontSize: '11px', color: '#374151', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ingredient</th>
                              <th style={{ padding: '10px 12px', fontSize: '11px', color: '#374151', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>Qty Consumed</th>
                              <th style={{ padding: '10px 12px', fontSize: '11px', color: '#374151', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>Estimated Cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topIngredients.map((ing, idx) => {
                              return (
                                <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6' }}>
                                  <td style={{ padding: '10px 12px', fontSize: '13px', fontWeight: 600, color: '#111827' }}>
                                    {ing.name}
                                  </td>
                                  <td style={{ padding: '10px 12px', fontSize: '13px', color: '#4B5563', textAlign: 'right' }}>
                                    {ing.qtyUsed?.toFixed(2)} {ing.unitType || ''}
                                  </td>
                                  <td style={{ padding: '10px 12px', fontSize: '13px', color: '#111827', fontWeight: 600, textAlign: 'right' }}>
                                    {formatCurrency(ing.cost || 0, currency)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </Box>
                    );
                  })()}
                </Card>
              </Grid>

              {/* Waste Analysis */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none', backgroundColor: '#fff', height: 420, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Waste Analysis</Typography>
                    <Button
                      variant="text"
                      onClick={() => setSelectedTab('wastage')}
                      sx={{ textTransform: 'none', color: '#3B82F6', fontWeight: 600, fontSize: '14px', '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' } }}
                    >
                      View All
                    </Button>
                  </Box>

                  {(() => {
                    const totalWasteCost = dashboardData.wasteAnalysis?.totalWasteCost || 0;
                    const totalWasteQty = dashboardData.wasteAnalysis?.totalWasteQty || 0;
                    const topWastedItem = dashboardData.wasteAnalysis?.topWastedItem || 'N/A';
                    const wasteReasons = dashboardData.wasteAnalysis?.wasteByReason || [];

                    if (totalWasteQty === 0) {
                      return (
                        <Box sx={{ display: 'flex', flexGrow: 1, alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                          No wastage logged during this period.
                        </Box>
                      );
                    }

                    const COLORS = ['#14B8A6', '#3B82F6', '#F97316', '#EAB308', '#A855F7'];

                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        {/* Upper Stats Row */}
                        <Grid container spacing={1.5} sx={{ mb: 3 }}>
                          <Grid item xs={4}>
                            <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, border: '1px solid #F3F4F6', borderRadius: '10px', height: '100%' }}>
                              <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#FCE8E6', color: '#C5221F', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="caption" sx={{ color: '#C5221F', fontWeight: 600, fontSize: '10px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Total Waste Cost</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111827', mt: 0.25 }}>
                                  {formatCurrency(totalWasteCost, currency)}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                          <Grid item xs={4}>
                            <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, border: '1px solid #F3F4F6', borderRadius: '10px', height: '100%' }}>
                              <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#FEF3C7', color: '#D97706', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <InventoryIcon sx={{ fontSize: 18 }} />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="caption" sx={{ color: '#D97706', fontWeight: 600, fontSize: '10px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Waste Quantity</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111827', mt: 0.25 }}>
                                  {totalWasteQty?.toFixed(2)} units
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                          <Grid item xs={4}>
                            <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, border: '1px solid #F3F4F6', borderRadius: '10px', height: '100%' }}>
                              <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#F3E8FF', color: '#7C3AED', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <DeleteIcon sx={{ fontSize: 18 }} />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="caption" sx={{ color: '#7C3AED', fontWeight: 600, fontSize: '10px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Top Wasted Item</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111827', mt: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {topWastedItem}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>

                        {/* Waste by Reason Donut Section */}
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', mb: 1 }}>Waste by Reason</Typography>
                        <Grid container spacing={2} sx={{ alignItems: 'center', flexGrow: 1 }}>
                          <Grid item xs={5} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Box sx={{ width: 130, height: 130 }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie
                                    data={wasteReasons}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={38}
                                    outerRadius={58}
                                    paddingAngle={2}
                                    dataKey="qty"
                                  >
                                    {wasteReasons.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip formatter={(value) => [`${value.toFixed(1)} units`, 'Qty']} />
                                </PieChart>
                              </ResponsiveContainer>
                            </Box>
                          </Grid>
                          <Grid item xs={7} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {wasteReasons.map((item, idx) => (
                              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: COLORS[idx % COLORS.length] }} />
                                  <Typography variant="body2" sx={{ color: '#4B5563', fontSize: '13px', fontWeight: 500 }}>
                                    {item.reason}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ color: '#1F2937', fontSize: '13px', fontWeight: 600 }}>
                                  {parseFloat(item.percentage || 0).toFixed(1)}%
                                </Typography>
                              </Box>
                            ))}
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  })()}
                </Card>
              </Grid>

              {/* Low Stock Alert */}
              <Grid item xs={12}>
                <Card sx={{ p: 3, borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none', backgroundColor: '#fff', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Low Stock Alert</Typography>
                    <Button
                      variant="text"
                      onClick={() => {
                        setSelectedTab('items');
                        setSelectedStatusFilter?.('lowstock');
                      }}
                      sx={{ textTransform: 'none', color: '#3B82F6', fontWeight: 600, fontSize: '14px', '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' } }}
                    >
                      View All
                    </Button>
                  </Box>

                  {(() => {
                    const lowStockItems = dashboardData.lowStockAlerts || [];
                    if (lowStockItems.length === 0) {
                      return (
                        <Box sx={{ display: 'flex', minHeight: 150, alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                          No low stock alerts. All items are sufficiently stocked!
                        </Box>
                      );
                    }

                    return (
                      <Box sx={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                              <th style={{ padding: '10px 12px', fontSize: '11px', color: '#374151', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Item Name</th>
                              <th style={{ padding: '10px 12px', fontSize: '11px', color: '#374151', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Category</th>
                              <th style={{ padding: '10px 12px', fontSize: '11px', color: '#374151', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Current Qty</th>
                              <th style={{ padding: '10px 12px', fontSize: '11px', color: '#374151', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Min. Level</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lowStockItems.map((item, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6' }}>
                                <td style={{ padding: '10px 12px', fontSize: '13px', fontWeight: 600, color: '#111827' }}>
                                  {item.name}
                                </td>
                                <td style={{ padding: '10px 12px', fontSize: '13px', color: '#4B5563' }}>
                                  {item.categoryName}
                                </td>
                                <td style={{ padding: '10px 12px', fontSize: '13px', color: '#EF4444', fontWeight: 600 }}>
                                  {item.currentQty?.toFixed(2)} {item.unitType || ''}
                                </td>
                                <td style={{ padding: '10px 12px', fontSize: '13px', color: '#111827' }}>
                                  {item.minLevel?.toFixed(2)} {item.unitType || ''}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Box>
                    );
                  })()}
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {selectedTab === 'categories' && (
          <Card className="main-card-container" sx={{ py: '5px !important', margin: '0px !important', backgroundColor: '#fff', boxShadow: 'none', borderRadius: '12px', minHeight: 'auto !important' }}>
            {showCategoriesEmpty ? (
              <Box className="body-wrapper" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                {isSearching ? (
                  <NoSearchFound
                    title="No categories match your search"
                    subtitle="Please try again with a different keyword."
                  />
                ) : (
                  <NoItemsCreated
                    title="No Inventory Categories Created Yet!"
                    subtitle="Add your first category to start cataloging items."
                  />
                )}
              </Box>
            ) : (
              <Grid container spacing={2}>
                {categoriesLoading
                  ? Array.from({ length: 6 }).map((_, idx) => (
                    <Grid item xs={12} sm={6} md={4} key={idx}>
                      <CategoryCardSkeleton />
                    </Grid>
                  ))
                  : filteredCategories.map((cat) => (
                    <Grid item xs={12} sm={6} md={4} key={cat.id}>
                      <CategoryCard
                        title={cat.name}
                        itemCount={cat.itemsCount || 0}
                        countLabel="items"
                        enabled={cat.active_status}
                        onEdit={() => handleEdit(cat)}
                        onDelete={() => handleDelete(cat)}
                        onToggle={() => handleToggleStatus(cat)}
                        toggleDisabled={categoryTogglingId === cat.id}
                      />
                    </Grid>
                  ))}
              </Grid>
            )}
          </Card>
        )}

        {selectedTab === 'items' && (
          <Card className="main-card-container" sx={{ padding: '0px !important', margin: '8px 0px !important', maxHeight: 'none !important', overflow: 'hidden !important', minHeight: 'auto !important' }}>
            {showItemsEmpty ? (
              <Box className="body-wrapper">
                {isSearching ? (
                  <NoSearchFound
                    title="No items match your search"
                    subtitle="Please try again with a different keyword."
                  />
                ) : (
                  <NoItemsCreated
                    title="No Inventory Items Created Yet!"
                    subtitle="Add items under categories to manage stock levels."
                  />
                )}
              </Box>
            ) : itemsLoading ? (
              <TableSkeleton />
            ) : (
              <CommonDataGrid
                columns={itemColumns}
                rows={items}
                rowCount={itemsCount}
                loading={false}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
              />
            )}
          </Card>
        )}

        {selectedTab === 'recipes' && (
          <Card className="main-card-container" sx={{ padding: '0px !important', margin: '8px 0px !important', maxHeight: 'none !important', overflow: 'hidden !important', minHeight: 'auto !important' }}>
            {showRecipesEmpty ? (
              <Box className="body-wrapper">
                {isSearching ? (
                  <NoSearchFound
                    title="No recipes match your search"
                    subtitle="Please try again with a different keyword."
                  />
                ) : (
                  <NoItemsCreated
                    title="No Recipes Configured Yet!"
                    subtitle="Create ingredient lists mapping inventory to food items."
                  />
                )}
              </Box>
            ) : recipesLoading ? (
              <TableSkeleton />
            ) : (
              <CommonDataGrid
                columns={recipeColumns}
                rows={recipes}
                rowCount={recipesCount}
                loading={false}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
              />
            )}
          </Card>
        )}

        {selectedTab === 'wastage' && (
          <Card className="main-card-container" sx={{ padding: showWastageEmpty ? '24px !important' : '0px !important', margin: '8px 0px !important', maxHeight: 'none !important', overflow: 'hidden !important', minHeight: 'auto !important' }}>
            {showWastageEmpty ? (
              <Box className="body-wrapper">
                {isSearching ? (
                  <NoSearchFound
                    title="No wastage records match your search"
                    subtitle="Please try again with a different keyword."
                  />
                ) : (
                  <NoItemsCreated
                    title="No Wastage Logged Yet"
                    subtitle="Recorded ingredients wastage reports will show up here."
                  />
                )}
              </Box>
            ) : wastagesLoading ? (
              <TableSkeleton />
            ) : (
              <CommonDataGrid
                columns={wastageColumns}
                rows={wastage}
                rowCount={wastageCount}
                loading={false}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
              />
            )}
          </Card>
        )}

        {selectedTab === 'adjustments' && (
          <Card className="main-card-container" sx={{ padding: showAdjustmentsEmpty ? '24px !important' : '0px !important', margin: '8px 0px !important', maxHeight: 'none !important', overflow: 'hidden !important', minHeight: 'auto !important' }}>
            {showAdjustmentsEmpty ? (
              <Box className="body-wrapper" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                {isSearching ? (
                  <NoSearchFound
                    title="No stock adjustments match your search"
                    subtitle="Please try again with a different keyword."
                  />
                ) : (
                  <NoItemsCreated
                    title="No Stock Adjustments Yet"
                    subtitle="Recorded stock adjustments will show up here."
                  />
                )}
              </Box>
            ) : adjustmentsLoading ? (
              <TableSkeleton />
            ) : (
              <CommonDataGrid
                columns={adjustmentColumns}
                rows={adjustments}
                rowCount={adjustmentsCount}
                loading={adjustmentsLoading}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
              />
            )}
          </Card>
        )}
      </Container>
    </MainContainer >
  );
};

export default InventoryContainer;
